import { ChangeEvent, ReactNode, useCallback, useId, useMemo } from 'react';
import { AccountCreationType, ConfigFormHandlers } from 'apps/cdp-web-demo/app/types';
import Image from 'next/image';
import { ControlGroup } from '@cbhq/cds-web/controls/ControlGroup';
import { Icon } from '@cbhq/cds-web/icons/Icon';
import { Box } from '@cbhq/cds-web/layout';
import { Tooltip } from '@cbhq/cds-web/overlays/tooltip/Tooltip';
import { Text } from '@cbhq/cds-web/typography';

import styles from './AccountCreationType.module.css';
import { Radio } from './AccountCreationTypeRadioInput';

type Props = {
  accountType: AccountCreationType;
  setAccountCreationType: ConfigFormHandlers['setAccountCreationType'];
};

function AccountCreationTypeField({ accountType, setAccountCreationType }: Props) {
  const labelId = useId();

  const accountCreationTypeOptions = useMemo(() => {
    const opts: {
      name: string;
      label: ReactNode;
      'aria-label': string;
      value: AccountCreationType;
      disabled?: boolean;
    }[] = [
      {
        'aria-label': 'EVM Smart Contract',
        name: 'accountCreationType',
        label: (
          <Box
            as="span"
            display="flex"
            flexGrow={1}
            justifyContent="start"
            alignItems="center"
            gap={1}
            padding={0}
          >
            <Image width={16} height={16} loading="lazy" src="/evm-icon.svg" alt="" />
            <Text font="label2" lineHeight="headline" className={styles['radio-icon-label']}>
              EVM Smart Contract
            </Text>
            <Tooltip content="A smart contract-based account that can provide advanced functionality such as gas sponsorships and spend permissions.">
              <Icon size="xs" color="fgMuted" name="info" />
            </Tooltip>
          </Box>
        ),
        value: 'evm-smart',
        // url: '',
      },
      {
        'aria-label': 'EVM EOA',
        name: 'accountCreationType',
        label: (
          <Box
            as="span"
            display="flex"
            flexGrow={1}
            justifyContent="start"
            alignItems="center"
            gap={1}
            padding={0}
          >
            <Image width={16} height={16} loading="lazy" src="/evm-icon.svg" alt="" />
            <Text font="label2" lineHeight="headline" className={styles['radio-icon-label']}>
              EVM EOA
            </Text>
            <Tooltip content="An Externally Owned Account (EOA) on any EVM-compatible blockchain that have the ability to sign transactions on behalf of an account’s address.">
              <Icon size="xs" color="fgMuted" name="info" />
            </Tooltip>
          </Box>
        ),
        value: 'evm-eoa',
        // url: '',
      },
      {
        'aria-label': 'Solana',
        name: 'accountCreationType',
        label: (
          <Box
            as="span"
            display="flex"
            flexGrow={1}
            justifyContent="start"
            alignItems="center"
            padding={0}
            gap={1}
          >
            <Image width={16} height={16} loading="lazy" src="/solana-icon.svg" alt="" />
            <Text font="label2" lineHeight="headline" className={styles['radio-icon-label']}>
              Solana
            </Text>
            <Tooltip content="An account on Solana devnet or mainnet that signs transactions on behalf of your onchain address.">
              <Icon size="xs" color="fgMuted" name="info" />
            </Tooltip>
          </Box>
        ),
        value: 'solana',
      },
    ];
    return opts;
  }, []);

  const handleAccountCreationTypeChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setAccountCreationType(e.target.value as AccountCreationType);
    },
    [setAccountCreationType],
  );

  return (
    <ControlGroup
      label={
        <Text id={labelId} font="label1" className={styles['radio-label']}>
          Account type
        </Text>
      }
      aria-labelledby={labelId}
      columnGap={1}
      ControlComponent={Radio}
      value={accountType}
      role="radiogroup"
      onChange={handleAccountCreationTypeChange}
      options={accountCreationTypeOptions}
      className={`${styles['radio-group']} ${styles['account-creation-type-group']}`}
    />
  );
}

export { AccountCreationTypeField };
